import { baseApi } from "../baseApi";
import {
  BeaconBundlesResponse,
  BeaconLibraryResponse,
  BeaconManifestResponse,
  BeaconNotesResponse,
  BeaconPreviewResponse,
  BeaconRecommendationsResponse,
  UpsertBeaconNoteRequest,
} from "./type";

export const beaconApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getBeaconBundles: build.query<BeaconBundlesResponse, void>({
      query: () => ({
        url: "/beacon/bundles",
        method: "GET",
      }),
      providesTags: ["BeaconBundles"],
    }),
    getBeaconLibrary: build.query<BeaconLibraryResponse, void>({
      query: () => ({
        url: "/beacon/library",
        method: "GET",
      }),
      providesTags: ["BeaconLibrary"],
    }),
    getBeaconBundleDetails: build.query<
      BeaconLibraryResponse["data"][number],
      string
    >({
      query: (bundleId) => ({
        url: `/beacon/bundles/${bundleId}`,
        method: "GET",
      }),
      transformResponse: (response: {
        statusCode: number;
        message: string;
        data: BeaconLibraryResponse["data"][number];
      }) => response.data,
      providesTags: (result, error, bundleId) => [
        { type: "BeaconLibrary", id: bundleId },
      ],
    }),
    getBeaconBundlePreview: build.query<BeaconPreviewResponse, string>({
      query: (bundleId) => ({
        url: `/beacon/bundles/${bundleId}/preview`,
        method: "GET",
      }),
    }),
    getBeaconAssetManifest: build.query<BeaconManifestResponse, string>({
      query: (assetId) => ({
        url: `/beacon/assets/${assetId}/manifest`,
        method: "GET",
      }),
      providesTags: (result, error, assetId) => [
        { type: "BeaconNotes", id: assetId },
      ],
    }),
    getBeaconAssetNotes: build.query<BeaconNotesResponse, string>({
      query: (assetId) => ({
        url: `/beacon/assets/${assetId}/notes`,
        method: "GET",
      }),
      providesTags: (result, error, assetId) => [
        { type: "BeaconNotes", id: assetId },
      ],
    }),
    upsertBeaconNote: build.mutation<BeaconNotesResponse, UpsertBeaconNoteRequest>(
      {
        query: ({ assetId, body }) => ({
          url: `/beacon/assets/${assetId}/notes`,
          method: "PATCH",
          body,
        }),
        invalidatesTags: (result, error, { assetId }) => [
          { type: "BeaconNotes", id: assetId },
        ],
      },
    ),
    resolveBeaconRecommendations: build.mutation<
      BeaconRecommendationsResponse,
      { text: string; childId?: string; source?: string; distressScore?: number }
    >({
      query: (body) => ({
        url: "/beacon/recommendations/resolve",
        method: "POST",
        body,
      }),
    }),
    acceptBeaconRecommendation: build.mutation<
      { statusCode: number; message: string; data: any },
      { bundleId: string; resourceId: string; evidence?: Record<string, any> }
    >({
      query: ({ bundleId, ...body }) => ({
        url: `/beacon/recommendations/${bundleId}/accept`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["BeaconLibrary", "BeaconBundles"],
    }),
    dismissBeaconRecommendation: build.mutation<
      { statusCode: number; message: string; data: any },
      { bundleId: string; resourceId?: string; evidence?: Record<string, any> }
    >({
      query: ({ bundleId, ...body }) => ({
        url: `/beacon/recommendations/${bundleId}/dismiss`,
        method: "POST",
        body,
      }),
    }),
  }),
});

export const {
  useGetBeaconBundlesQuery,
  useGetBeaconLibraryQuery,
  useGetBeaconBundleDetailsQuery,
  useGetBeaconBundlePreviewQuery,
  useGetBeaconAssetManifestQuery,
  useGetBeaconAssetNotesQuery,
  useUpsertBeaconNoteMutation,
  useResolveBeaconRecommendationsMutation,
  useAcceptBeaconRecommendationMutation,
  useDismissBeaconRecommendationMutation,
} = beaconApi;
